import { AlertLooks, StandardAlert } from '@brandfolder/react';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useState, FunctionComponent, useEffect } from 'react';
import { object, SchemaOf, string } from 'yup';

import { useFetch } from '@api/ApiHelper';
import { CollectionsListResponse } from '@api/v4/collections';
import { SectionsListResponse } from '@api/v4/brandfolders/SectionTypes';
import { GcsIngestSourceResponse, GcsIngestSourceType } from '@api/v4/gcs-ingest-sources';

import { PrimaryButton } from '@components/library/button';
import { LabelPosition, StandardSwitch, SwitchSize } from '@components/library/switch';
import { ListDropdown } from '@components/library/dropdown';
import { InputType, StandardTextField } from '@components/library/text-field';
import { BFLoader } from '@components/common/loader/main';

import './styles/sftp-ingest.scss';
interface SftpFormValues {
  password: string;
  sectionKey: string;
  rootdir: string;
  collectionKey?: string;
}

const validationSchema: SchemaOf<SftpFormValues> = object().shape({
  collectionKey: string(),
  sectionKey: string()
    .required(bfTranslate('Section is required')),
  rootdir: string()
    .min(3, bfTranslate('Minimum 3 characters'))
    .max(200, bfTranslate('Maximum 200 characters'))
    .matches(/^[a-z0-9_-]+$/i, 'No special characters or spaces allowed')
    .required(bfTranslate('Name is required')),
  password: string()
    .min(8, bfTranslate('Minimum 8 characters'))
    .max(100, bfTranslate('Maximum 100 characters'))
    .required(bfTranslate('Password is required'))
});

const CreateSftpIngestForm: FunctionComponent<{ setShowForm: SetStateDispatch<boolean> }> = ({ setShowForm }) => {
  const [allowFtp, setAllowFtp] = useState(false);
  const [submissionValues, setSubmissionValues] = useState<SftpFormValues | undefined>(undefined);

  const collectionsFetch = useFetch<CollectionsListResponse>({
    url: `/api/v4/brandfolders/${BFG.resource.key}/collections`
  });
  const sectionsFetch = useFetch<SectionsListResponse>({
    url: `/api/v4/brandfolders/${BFG.resource.key}/sections`,
    params: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      default_asset_type: 'GenericFile'
    }
  });
  const createGcsIngestSourceFetch = useFetch<GcsIngestSourceResponse>({
    url: `/api/v4/sections/${submissionValues?.sectionKey}/gcs_ingest_sources`,
    body: {
      data: {
        attributes: {
          rootdir: submissionValues?.rootdir,
          password: submissionValues?.password,
          ftp_permission: allowFtp, // eslint-disable-line @typescript-eslint/naming-convention
          // eslint-disable-next-line @typescript-eslint/naming-convention
          collection_key: submissionValues?.collectionKey || undefined,
          type: GcsIngestSourceType.SFTP
        }
      }
    },
    method: 'POST',
    fetchOnMount: false
  });

  useEffect(() => {
    if (submissionValues) {
      createGcsIngestSourceFetch.fetch();
    }
  }, [submissionValues]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (createGcsIngestSourceFetch.response) {
      setShowForm(false);
    }
  }, [createGcsIngestSourceFetch.response]);

  const collectionsLoaded = collectionsFetch.response && !collectionsFetch.error;
  const collectionOptions = collectionsLoaded
    ? collectionsFetch.response.data.map((collection) => ({ label: collection.attributes.name, value: collection.id }))
    : [];

  const sectionsLoaded = sectionsFetch.response && !sectionsFetch.error;
  const sectionOptions = sectionsLoaded
    ? sectionsFetch.response.data.map((section) => ({ label: section.attributes.name, value: section.id }))
    : [];

  return (
    <div className="ftp-ingest-form">
      {createGcsIngestSourceFetch.error && (
        <StandardAlert
          className="ftp-ingest-error-message"
          heading={bfTranslate('Oops!')}
          look={AlertLooks.Error}
          showHeading
          showIcon
        >
          {allowFtp
            ? bfTranslate('There was an error with your FTP creation. Please try again with a unique FTP name.')
            : bfTranslate('There was an error with your SFTP creation. Please try again with a unique SFTP name.')}
        </StandardAlert>
      )}
      {BFG.getty_client && (
        <div className="ftp-ingest-toggle-container">
          <StandardSwitch
            id="sftp-ingest-allow-ftp"
            isChecked={allowFtp}
            labelPosition={LabelPosition.Right}
            onChange={(): void => setAllowFtp(!allowFtp)}
            size={SwitchSize.Large}
          >
            {bfTranslate('Allow insecure FTP')}
          </StandardSwitch>
        </div>
      )}
      {sectionsFetch.response ? (
        <Formik
          initialValues={{
            rootdir: '',
            password: '',
            sectionKey: sectionsFetch.response?.data[0]?.id || '',
            collectionKey: ''
          } as SftpFormValues}
          onSubmit={async (values: SftpFormValues, { validateForm }: FormikHelpers<SftpFormValues>): Promise<void> => {
            const validationErrors = await validateForm();
            if (!validationErrors.rootdir && !validationErrors.sectionKey && !validationErrors.rootdir) {
              setSubmissionValues(values);
            }
          }}
          validateOnChange
          validationSchema={validationSchema}
        >
          {(formikBag): JSX.Element => {
            const { errors, touched, values, setFieldValue, setFieldTouched } = formikBag;

            return (
              <Form id="sftp-ingest-form">
                <div className="ftp-ingest-form__input-container">
                  <StandardTextField
                    autoComplete='off'
                    error={touched.rootdir && errors.rootdir}
                    id="ftp-ingest-form__rootdir-input"
                    label={allowFtp ? bfTranslate('FTP name') : bfTranslate('SFTP name')}
                    maxLength={200}
                    onBlur={(): void => {
                      setFieldTouched('rootdir');
                    }}
                    onChange={(e: InputChangeEvent): void => {
                      setFieldValue('rootdir', e.target.value);
                    }}
                    placeholder=""
                    value={values.rootdir}
                  />
                </div>
                <div className="ftp-ingest-form__input-container">
                  <StandardTextField
                    autoComplete='off'
                    error={touched.password && errors.password}
                    id="ftp-ingest-form__password-input"
                    label={bfTranslate('Password')}
                    maxLength={100}
                    onBlur={(): void => {
                      setFieldTouched('password');
                    }}
                    onChange={(e: InputChangeEvent): void => {
                      setFieldValue('password', e.target.value);
                    }}
                    placeholder=""
                    type={InputType.Password}
                    value={values.password}
                  />
                  <div className="ftp-ingest-form__input-container--warning">
                    <div className="bff-warning ftp-ingest-form__input-container--warning-icon" />
                    <div>
                      <p>{bfTranslate('Passwords cannot be recovered or reset.')}</p>
                      <p>{bfTranslate('Please make a record of your password.')}</p>
                    </div>
                  </div>
                </div>
                <div className="ftp-ingest-form__input-container">
                  <ListDropdown
                    error={touched.sectionKey && errors.sectionKey}
                    isLoading={sectionsFetch.loading}
                    label={bfTranslate('Add assets to section')}
                    onChange={(listOption): void => {
                      setFieldValue('sectionKey', listOption.value as string);
                      setFieldTouched('sectionKey');
                    }}
                    options={sectionOptions}
                    searchable
                    value={values.sectionKey || sectionsFetch.response?.data[0]?.id}
                    virtualizeOptions={false}
                  />
                </div>
                {collectionOptions.length > 0 && (
                  <div className="ftp-ingest-form__input-container">
                    <ListDropdown
                      error={touched.collectionKey && errors.collectionKey}
                      isLoading={collectionsFetch.loading}
                      label={bfTranslate('Add assets to Collection (optional)')}
                      onChange={(listOption): void => {
                        setFieldValue('collectionKey', listOption.value as string);
                        setFieldTouched('collectionKey');
                      }}
                      options={collectionOptions}
                      placeholder={bfTranslate('Select Collection')}
                      searchable
                      value={values.collectionKey || undefined}
                      virtualizeOptions={false}
                    />
                  </div>
                )}
                <PrimaryButton
                  className="ftp-ingest-form__submit-button"
                  isLoading={createGcsIngestSourceFetch.loading}
                  loadingCopy={allowFtp ? bfTranslate('Creating FTP ingest') : bfTranslate('Creating SFTP ingest')}
                  size="medium"
                  type="submit"
                >
                  {allowFtp ? bfTranslate('Create FTP ingest') : bfTranslate('Create SFTP ingest')}
                </PrimaryButton>
              </Form>
            );
          }}
        </Formik>
      ) : <BFLoader />}
    </div>
  );
};

export default CreateSftpIngestForm;
